import MainComputationCalculator from "../../../../builders/calculator/mainComputation";
import { currencyRateConversion } from "../../../../utils/hooks/dataHandler";
import { createExchangeRateResolver } from "../shared";
import { buildIrasMainCompSections } from "./buildIrasMainCompSections";
import { resolveIrasChargeableIncomeSummary } from "./chargeableIncomeSummary";

const isSameAmount = (left, right) =>
  Math.abs((parseFloat(left) || 0) - (parseFloat(right) || 0)) < 0.01;

const hasDisplayRows = (section) =>
  Array.isArray(section.rows) && section.rows.length > 0;

const shouldRenderSection = (section) =>
  section.type === "result" ||
  hasDisplayRows(section) ||
  Number(section.total || 0) !== 0;

const shouldRenderNonResultSection = (section) =>
  section.type !== "result" &&
  (hasDisplayRows(section) || Number(section.total || 0) !== 0);

const hasRenderableSectionBeforeNextResult = (sections, resultIndex) => {
  for (let index = resultIndex + 1; index < sections.length; index++) {
    const section = sections[index];
    if (section.type === "result") return false;
    if (shouldRenderNonResultSection(section)) return true;
  }

  return false;
};

const shouldRenderResultSection = (sections, section, index) =>
  section.alwaysRender ||
  section.id === "net_profit_before_tax" ||
  section.id === "total_after_donations" ||
  hasRenderableSectionBeforeNextResult(sections, index);

export const isIrasChargeableIncomeSection = (section) =>
  ["total_after_donations", "chargeable_income_before_exempt_amount"].includes(
    section?.id,
  );

export const getRenderableIrasMainCompSections = (sections = []) =>
  sections.filter((section, index) =>
    section.type === "result"
      ? shouldRenderResultSection(sections, section, index)
      : shouldRenderSection(section)
  );

export const calculateIrasSgdSectionResults = ({
  currencyRate,
  sections = [],
  unutilisedCapitalAllowances = [],
  unutilisedDonations = [],
  unutilisedLosses = [],
}) => {
  const exchangeRate = currencyRate ?? 1;
  const getExchangeRate = createExchangeRateResolver(exchangeRate);
  const convertAmount = (amount, rate = exchangeRate) =>
    Math.round(currencyRateConversion(amount, rate, "SGD"));
  const sectionAmounts = new WeakMap();
  const resultAmounts = new WeakMap();
  const rowAmounts = new WeakMap();
  const resultAmountsById = new Map();
  let previousResultAmount = null;
  let previousResultSgdAmount = null;
  let pendingSectionAmount = 0;
  let pendingSectionSgdAmount = 0;
  let chargeableIncomeBeforeExemptAmount = null;

  sections.forEach((section) => {
    if (section.type !== "result") {
      const sectionSgdAmount =
        section.rows?.length > 0
          ? section.rows.reduce(
              (total, row) => {
                // Reused results must retain the rounding of their source rows.
                const rowSgdAmount =
                  resultAmountsById.get(row.sourceResultId) ??
                  convertAmount(
                    parseFloat(row.amount) || 0,
                    getExchangeRate(row.exchangeRate)
                  );
                rowAmounts.set(row, rowSgdAmount);
                return total + rowSgdAmount;
              },
              0
            )
          : convertAmount(parseFloat(section.total) || 0);

      sectionAmounts.set(section, sectionSgdAmount);
      pendingSectionAmount += parseFloat(section.total) || 0;
      pendingSectionSgdAmount += sectionSgdAmount;
      return;
    }

    const amount = parseFloat(section.amount) || 0;
    const directlyConvertedResult =
      isIrasChargeableIncomeSection(section)
        ? Math.round(
            MainComputationCalculator.convertChargeableIncomeToSgd(
              amount,
              exchangeRate,
              [
                ...unutilisedCapitalAllowances,
                ...unutilisedLosses,
                ...unutilisedDonations,
              ]
            )
          )
        : convertAmount(amount);
    let resultSgdAmount = directlyConvertedResult;

    if (
      previousResultSgdAmount !== null &&
      isSameAmount(amount, previousResultAmount + pendingSectionAmount)
    ) {
      resultSgdAmount = previousResultSgdAmount + pendingSectionSgdAmount;
    } else if (
      previousResultSgdAmount !== null &&
      isSameAmount(amount, pendingSectionAmount)
    ) {
      resultSgdAmount = pendingSectionSgdAmount;
    }

    resultAmounts.set(section, resultSgdAmount);
    resultAmountsById.set(section.id, resultSgdAmount);
    previousResultAmount = amount;
    previousResultSgdAmount = resultSgdAmount;
    pendingSectionAmount = 0;
    pendingSectionSgdAmount = 0;

    if (isIrasChargeableIncomeSection(section)) {
      chargeableIncomeBeforeExemptAmount = Math.max(0, resultSgdAmount);
    }
  });

  return {
    chargeableIncomeBeforeExemptAmount,
    resultAmounts,
    rowAmounts,
    sectionAmounts,
  };
};

export const calculateIrasNonSgdReferenceValues = ({
  corporateIncomeTaxRebate = {},
  currencyRate,
  currentYearRebate = {},
  isStartup,
  netProfitBeforeTax,
  referenceData = {},
  taxPreviouslyAssessed = [],
  unutilisedCapitalAllowances = [],
  unutilisedDonations = [],
  unutilisedLosses = [],
}) => {
  const sections = getRenderableIrasMainCompSections(
    buildIrasMainCompSections({
      netProfitBeforeTax,
      referenceData,
      unutilisedCapitalAllowances,
      unutilisedDonations,
      unutilisedLosses,
    })
  );
  const calculation = calculateIrasSgdSectionResults({
    currencyRate,
    sections,
    unutilisedCapitalAllowances,
    unutilisedDonations,
    unutilisedLosses,
  });
  const sectionValues = sections.reduce((values, section) => {
    const amount =
      section.type === "result"
        ? calculation.resultAmounts.get(section)
        : calculation.sectionAmounts.get(section);

    if (typeof amount === "number") values[section.id] = amount;
    return values;
  }, {});
  const taxSummary = resolveIrasChargeableIncomeSummary({
    chargeableIncomeBeforeExemptAmount:
      calculation.chargeableIncomeBeforeExemptAmount ?? 0,
    corporateIncomeTaxRebate,
    currentYearRebate,
    isStartup,
    taxPreviouslyAssessed,
  });

  return { sectionValues, taxSummary };
};
